import React, { useCallback, useEffect, useMemo, useState } from "react";
import { DataTable, TableCellEllipsis } from "../../ui";
import { MDBContainer } from "mdb-react-ui-kit";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faArrowUpRightFromSquare,
  faCheck,
  faCopy,
  faEye,
} from "@fortawesome/free-solid-svg-icons";
import Toolbar from "../../Toolbar";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { api } from "../../../services";
import PriceDisplay from "../../PriceDisplay";
import OrderStatsCard from "../../OrdersReportComponents/OrderStatsCard";
import OrderDetailsDrawer from "../../OrderDetailsDrawer";
import {
  fetchTrackHubReports,
  fetchTrackHubReportsExport,
} from "../../../services/trackHubApi";
import {
  resolveDateLocale,
  formatLocalizedDateTimeParts,
} from "../../../utils/formatDateTime";
import "../../trackhub/trackhub-reports.css";
import CourierInfoCell from "../../trackhub/CourierInfoCell";
import ShipmentPaymentCell from "../../trackhub/ShipmentPaymentCell";
import ShipmentDocumentButtons from "../../trackhub/ShipmentDocumentButtons";

const STATUS_CLASS = {
  Delivered: "trackhub-reports-status--delivered",
  "In Transit": "trackhub-reports-status--in-transit",
  Assigned: "trackhub-reports-status--assigned",
  Pending: "trackhub-reports-status--pending",
  "Pending Payment": "trackhub-reports-status--pending",
  Scheduled: "trackhub-reports-status--pending",
  Cancelled: "trackhub-reports-status--cancelled",
};

function StatusBadge({ status, label }) {
  const cls = STATUS_CLASS[status] || "trackhub-reports-status--pending";
  return (
    <span className={`trackhub-reports-status ${cls}`}>{label || status}</span>
  );
}

function ShipmentIdCell({ shipmentId, copyLabel, copiedLabel }) {
  const [copied, setCopied] = useState(false);

  if (!shipmentId) {
    return <span className="trackhub-reports-mono">—</span>;
  }

  const handleCopy = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(shipmentId);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1500);
    } catch (err) {
      console.error("Failed to copy shipment id", err);
    }
  };

  return (
    <div
      className="trackhub-reports-id-cell"
      dir="ltr"
      onClick={(e) => e.stopPropagation()}
    >
      <TableCellEllipsis title={shipmentId}>{shipmentId}</TableCellEllipsis>
      <button
        type="button"
        className="trackhub-reports-copy-btn"
        onClick={handleCopy}
        title={copied ? copiedLabel : copyLabel}
        aria-label={copied ? copiedLabel : copyLabel}
      >
        <FontAwesomeIcon icon={copied ? faCheck : faCopy} />
      </button>
    </div>
  );
}

function defaultLast30Days() {
  const to = new Date();
  to.setHours(0, 0, 0, 0);
  const from = new Date(to);
  from.setDate(from.getDate() - 29);
  const fmt = (d) => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${y}-${m}-${day}`;
  };
  return { dateFrom: fmt(from), dateTo: fmt(to) };
}

function formatOrderSource(source, t) {
  const s = String(source || "").toUpperCase();
  if (s.includes("CATALOG") || s === "DIGITAL_CATALOG") {
    return t("shipmentsPage.orderSourceDigitalCatalog");
  }
  if (
    s.includes("CHAT") ||
    s.includes("ORDER_AGENT") ||
    s === "WHATSAPP_ORDER_AGENT"
  ) {
    return t("shipmentsPage.orderSourceOrderAgent");
  }
  return source || "—";
}

function statusLabel(status, t) {
  const map = {
    Delivered: t("shipmentsPage.statusDelivered"),
    "In Transit": t("shipmentsPage.statusInTransit"),
    Assigned: t("shipmentsPage.statusAssigned"),
    Pending: t("shipmentsPage.statusPending"),
    "Pending Payment": t("shipmentsPage.statusPendingPayment"),
    Scheduled: t("shipmentsPage.statusScheduled"),
    Cancelled: t("shipmentsPage.statusCancelled"),
  };
  return map[status] || status;
}

function AdminShipmentsPage() {
  const { t, i18n } = useTranslation("translation");
  const isArabic =
    String(i18n.language || "").toLowerCase().startsWith("ar") ||
    i18n.dir() === "rtl";
  const dateLocale = resolveDateLocale(i18n.language);

  const [loading, setLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [filterOptions, setFilterOptions] = useState({
    carriers: [],
    businessAccounts: [],
  });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({});
  const [dateRange, setDateRange] = useState(defaultLast30Days);
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedCarrier, setSelectedCarrier] = useState("ALL");
  const [searchQuery, setSearchQuery] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortDirection, setSortDirection] = useState("desc");
  const [selectedBusinessAccountId, setSelectedBusinessAccountId] =
    useState("");
  const [selectedStoreId, setSelectedStoreId] = useState("");
  const [selectedBranchId, setSelectedBranchId] = useState("");
  const [storesOptions, setStoresOptions] = useState([]);
  const [branchesOptions, setBranchesOptions] = useState([]);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [selectedOrderDetails, setSelectedOrderDetails] = useState(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchQuery);
      setPage(1);
    }, 500);
    return () => clearTimeout(timer);
  }, [searchQuery]);

  const loadStores = useCallback(async () => {
    try {
      const res = await api.get("/stores", {
        useAdminAuth: true,
        params: { page: 1, limit: 100 },
      });
      const stores = res.data?.data || res.data?.stores || [];
      setStoresOptions(
        stores.map((s) => ({
          _id: s._id || s.id,
          name: s.name || s.name_en || s.displayName || "",
          name_en: s.name || s.name_en || s.displayName || "",
          name_ar: s.name_ar || s.displayNameAr || s.name || "",
          adminId: s.admin?._id || s.admin || null,
          branches: s.branches || [],
        }))
      );
    } catch (error) {
      console.error("Failed to load stores for Admin Shipments", error);
    }
  }, []);

  const loadReports = useCallback(async () => {
    setLoading(true);
    try {
      const result = await fetchTrackHubReports(
        {
          page,
          limit,
          status: selectedStatus,
          carrier: selectedCarrier,
          businessAccountId: selectedBusinessAccountId || undefined,
          storeId: selectedStoreId || undefined,
          branchId: selectedBranchId || undefined,
          dateFrom: dateRange.dateFrom || undefined,
          dateTo: dateRange.dateTo || undefined,
          search: debouncedSearch || undefined,
          sortBy: sortColumn || "createdAt",
          sortOrder: sortDirection || "desc",
        },
        { useAdminAuth: true }
      );
      setRows(result.data || []);
      setSummary(result.summary || {});
      setFilterOptions(
        result.filterOptions || { carriers: [], businessAccounts: [] }
      );
      setPagination(result.pagination || {});
    } catch (error) {
      console.error("Failed to load Admin Shipments", error);
      toast.error(t("adminShipmentsPage.loadError"));
    } finally {
      setLoading(false);
    }
  }, [
    page,
    limit,
    selectedStatus,
    selectedCarrier,
    selectedBusinessAccountId,
    selectedStoreId,
    selectedBranchId,
    dateRange.dateFrom,
    dateRange.dateTo,
    debouncedSearch,
    sortColumn,
    sortDirection,
    t,
  ]);

  useEffect(() => {
    loadStores();
  }, [loadStores]);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const filteredStores = useMemo(() => {
    if (!selectedBusinessAccountId) return storesOptions;
    return storesOptions.filter(
      (s) => String(s.adminId || "") === String(selectedBusinessAccountId)
    );
  }, [storesOptions, selectedBusinessAccountId]);

  useEffect(() => {
    if (
      selectedStoreId &&
      !filteredStores.some((s) => String(s._id) === String(selectedStoreId))
    ) {
      setSelectedStoreId("");
      setSelectedBranchId("");
      setBranchesOptions([]);
      return;
    }
    const store = filteredStores.find(
      (s) => String(s._id) === String(selectedStoreId)
    );
    if (!store) {
      setBranchesOptions([]);
      return;
    }
    setBranchesOptions(
      (store.branches || []).map((b) => ({
        _id: b._id || b.id,
        name: b?.name?.en || b?.name || b?.displayName || "",
        name_en: b?.name?.en || b?.name || b?.displayName || "",
        name_ar: b?.name?.ar || b?.name_ar || b?.displayNameAr || b?.name || "",
      }))
    );
  }, [selectedStoreId, filteredStores]);

  const businessAccountOptions = useMemo(
    () => [
      {
        _id: "ALL",
        name: t("adminShipmentsPage.allBusinessAccounts"),
      },
      ...(filterOptions.businessAccounts || []).map((account) => ({
        _id: account.id,
        name: account.name || account.businessId || account.id,
      })),
    ],
    [filterOptions.businessAccounts, t]
  );

  const reportStatuses = useMemo(
    () => [
      { id: "ALL", name: t("all") },
      { id: "Delivered", name: t("shipmentsPage.statusDelivered") },
      { id: "In Transit", name: t("shipmentsPage.statusInTransit") },
      { id: "Assigned", name: t("shipmentsPage.statusAssigned") },
      { id: "Pending", name: t("shipmentsPage.statusPending") },
      { id: "Pending Payment", name: t("shipmentsPage.statusPendingPayment") },
      { id: "Cancelled", name: t("shipmentsPage.statusCancelled") },
    ],
    [t]
  );

  const carrierOptions = useMemo(
    () => [
      { _id: "ALL", name: t("all") },
      ...(filterOptions.carriers || []).map((carrier) => ({
        _id: carrier,
        name: carrier,
      })),
    ],
    [filterOptions.carriers, t]
  );

  const handleExport = async () => {
    try {
      setIsExporting(true);
      const { blob, filename } = await fetchTrackHubReportsExport(
        {
          status: selectedStatus,
          carrier: selectedCarrier,
          businessAccountId: selectedBusinessAccountId || undefined,
          storeId: selectedStoreId || undefined,
          branchId: selectedBranchId || undefined,
          dateFrom: dateRange.dateFrom || undefined,
          dateTo: dateRange.dateTo || undefined,
          search: debouncedSearch || undefined,
          sortBy: sortColumn || "createdAt",
          sortOrder: sortDirection || "desc",
        },
        { useAdminAuth: true }
      );
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Error exporting admin shipments:", err);
      toast.error(t("adminShipmentsPage.exportError"));
    } finally {
      setIsExporting(false);
    }
  };

  const handleViewOrder = async (row) => {
    // GET /orders/:id looks up business order_id (same as Orders page), not Mongo _id
    const orderId = row.orderRef || row.orderId;
    if (!orderId) {
      toast.error(t("adminShipmentsPage.viewOrderMissing"));
      return;
    }
    try {
      const response = await api.get(`/orders/${orderId}`, {
        useAdminAuth: true,
      });
      const details = response.data?.data || response.data;
      if (!details) {
        toast.error(t("adminShipmentsPage.viewOrderMissing"));
        return;
      }
      setSelectedOrderDetails(details);
      setDrawerOpen(true);
    } catch (error) {
      console.error("Failed to load order for View", error);
      toast.error(t("adminShipmentsPage.viewOrderError"));
    }
  };

  const columns = [
    {
      id: "rowIndex",
      header: "#",
      minWidth: 48,
      width: 48,
      align: "center",
      headerAlign: "center",
      hideable: false,
      render: (_row, index) => (page - 1) * limit + index + 1,
    },
    {
      id: "businessId",
      header: t("adminShipmentsPage.businessId"),
      minWidth: 130,
      render: (row) => (
        <span className="trackhub-reports-mono" dir="ltr">
          {row.businessId || "—"}
        </span>
      ),
    },
    {
      id: "brandBranch",
      header: t("shipmentsPage.brandAndBranch"),
      minWidth: 150,
      render: (row) => (
        <div
          className={`trackhub-reports-brand-branch${
            isArabic ? " trackhub-reports-brand-branch--rtl" : ""
          }`}
        >
          <span className="trackhub-reports-brand">{row.brand || "—"}</span>
          <span className="trackhub-reports-branch">{row.branch || "—"}</span>
        </div>
      ),
    },
    {
      id: "orderRef",
      header: t("shipmentsPage.orderId"),
      minWidth: 120,
      render: (row) => (
        <span className="trackhub-reports-mono" dir="ltr">
          {row.orderRef || "—"}
        </span>
      ),
    },
    {
      id: "deliveryId",
      header: t("shipmentsPage.deliveryId"),
      minWidth: 120,
      render: (row) => {
        const deliveryId = row.deliveryId || "—";
        return (
          <span dir="ltr">
            <TableCellEllipsis title={row.deliveryId ? deliveryId : undefined}>
              {deliveryId}
            </TableCellEllipsis>
          </span>
        );
      },
    },
    {
      id: "orderSource",
      header: t("shipmentsPage.orderSource"),
      minWidth: 130,
      render: (row) => formatOrderSource(row.orderSource, t),
    },
    {
      id: "shipmentId",
      header: t("shipmentsPage.shipmentId"),
      minWidth: 150,
      render: (row) => (
        <ShipmentIdCell
          shipmentId={row.shipmentId || (row.status === "Pending Payment" ? row.orderRef : "")}
          copyLabel={t("shipmentsPage.copyShipmentId")}
          copiedLabel={t("shipmentsPage.shipmentIdCopied")}
        />
      ),
    },
    {
      id: "createdAt",
      header: t("shipmentsPage.date"),
      minWidth: 118,
      sortable: true,
      render: (row) => {
        const { date, time } = formatLocalizedDateTimeParts(
          row.createdAt,
          dateLocale,
          { withSeconds: false }
        );
        return (
          <div className="oh-table-date-cell" dir="ltr">
            <span className="oh-table-date-cell__date">{date}</span>
            <span className="oh-table-date-cell__time">{time}</span>
          </div>
        );
      },
    },
    {
      id: "status",
      header: t("shipmentsPage.status"),
      minWidth: 168,
      render: (row) => (
        <StatusBadge
          status={row.status}
          label={statusLabel(row.status, t)}
        />
      ),
    },
    {
      id: "payment",
      header: t("shipmentsPage.payment"),
      minWidth: 168,
      render: (row) => (
        <ShipmentPaymentCell row={row} t={t} dateLocale={dateLocale} />
      ),
    },
    {
      id: "carrier",
      header: t("shipmentsPage.courier"),
      minWidth: 160,
      render: (row) => <CourierInfoCell row={row} />,
    },
    {
      id: "orderTotal",
      header: t("shipmentsPage.orderTotal"),
      minWidth: 112,
      render: (row) => <PriceDisplay value={row.orderTotal ?? 0} />,
    },
    {
      id: "price",
      header: t("shipmentsPage.shipmentAmount"),
      minWidth: 148,
      render: (row) => <PriceDisplay value={row.price ?? 0} />,
    },
    {
      id: "action",
      header: t("shipmentsPage.action"),
      minWidth: 160,
      width: 160,
      align: "center",
      headerAlign: "center",
      hideable: false,
      render: (row) => (
        <div
          className="trackhub-reports-actions"
          onClick={(e) => e.stopPropagation()}
        >
          {row.paymentStatus ? (
            <ShipmentDocumentButtons row={row} t={t} language={i18n.language} />
          ) : null}
          <button
            type="button"
            className="trackhub-reports-view-btn"
            onClick={(e) => {
              e.stopPropagation();
              handleViewOrder(row);
            }}
          >
            <FontAwesomeIcon icon={faEye} />
            <span>{t("shipmentsPage.view")}</span>
          </button>
        </div>
      ),
    },
  ];

  const exportPrimaryAction = {
    label: isExporting
      ? t("shipmentsPage.exporting")
      : t("shipmentsPage.export"),
    onClick: handleExport,
    disabled: isExporting,
    icon: (
      <FontAwesomeIcon
        icon={faArrowUpRightFromSquare}
        style={{ transform: isArabic ? "scaleX(-1)" : "none" }}
      />
    ),
  };

  return (
    <div className="trackhub-reports-page oh-page">
      <MDBContainer fluid className="content-area flex-grow-1">
        <h1 className="page-title-header">{t("adminShipmentsPage.title")}</h1>

        <div className="oh-page-section">
          <div className="oh-kpi-row oh-kpi-row--4">
            <OrderStatsCard
              title={t("shipmentsPage.totalShipments")}
              value={summary.totalShipments ?? summary.totalDeliveries ?? 0}
              loading={loading}
            />
            <OrderStatsCard
              title={t("shipmentsPage.delivered")}
              value={summary.delivered ?? summary.deliveredCount ?? 0}
              loading={loading}
            />
            <OrderStatsCard
              title={t("shipmentsPage.failed")}
              value={summary.failed ?? 0}
              loading={loading}
            />
            <OrderStatsCard
              title={t("shipmentsPage.returned")}
              value={summary.returned ?? 0}
              loading={loading}
            />
          </div>
        </div>

        <div className="oh-page-section">
          <Toolbar
            toolbarVariant="shipments"
            hideIcons
            showSearch
            searchQuery={searchQuery}
            handleSearch={(e) => setSearchQuery(e.target.value)}
            searchPlaceHolder={t("adminShipmentsPage.searchPlaceholder")}
            extraDropdowns={[
              {
                type: "date-range",
                label: t("datePresets.customRange"),
                placeholder: t("datePresets.last30"),
                value: [dateRange.dateFrom || null, dateRange.dateTo || null],
                onChange: (val) => {
                  const [from, to] = val || [];
                  setDateRange({
                    dateFrom: from || "",
                    dateTo: to || "",
                  });
                  setPage(1);
                },
              },
              {
                label: t("adminShipmentsPage.businessAccount"),
                placeholder: t("adminShipmentsPage.businessAccount"),
                options: businessAccountOptions,
                selectedOption: selectedBusinessAccountId || "ALL",
                showClear: false,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  const id = opt?._id === "ALL" ? "" : opt?._id || "";
                  setSelectedBusinessAccountId(id);
                  setSelectedStoreId("");
                  setSelectedBranchId("");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.brand"),
                placeholder: t("trackHubBranchesPage.allBrands"),
                options: filteredStores,
                selectedOption: selectedStoreId,
                showClear: true,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedStoreId(opt?._id || "");
                  setSelectedBranchId("");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.branch"),
                placeholder: selectedStoreId
                  ? t("trackHubBranchesPage.allBranches")
                  : t("shipmentsPage.selectBrandFirst"),
                options: branchesOptions,
                selectedOption: selectedBranchId,
                showClear: true,
                disabled: !selectedStoreId,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedBranchId(opt?._id || "");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.courier"),
                placeholder: t("shipmentsPage.courier"),
                options: carrierOptions,
                selectedOption: selectedCarrier || "ALL",
                showClear: false,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedCarrier(opt?._id || "ALL");
                  setPage(1);
                },
              },
              {
                label: t("shipmentsPage.status"),
                placeholder: t("shipmentsPage.status"),
                options: reportStatuses.map((s) => ({
                  _id: s.id,
                  name: s.name,
                })),
                selectedOption: selectedStatus || "ALL",
                showClear: false,
                backgroundColor: "#ffffff",
                handleSelect: (opt) => {
                  setSelectedStatus(opt?._id || "ALL");
                  setPage(1);
                },
              },
            ]}
          />

          <DataTable
            tableId="admin-shipments"
            title={t("adminShipmentsPage.tableTitle")}
            columns={columns}
            data={rows}
            loading={loading}
            emptyState={t("adminShipmentsPage.empty")}
            primaryAction={exportPrimaryAction}
            showColumnSettings
            tableMinWidth={1520}
            isRtl={isArabic}
            sort={{
              column: sortColumn,
              direction: sortDirection,
              onChange: ({ column, direction }) => {
                setSortColumn(column || "createdAt");
                setSortDirection(direction === "asc" ? "asc" : "desc");
                setPage(1);
              },
            }}
            pagination={{
              page,
              limit,
              total: pagination.total || 0,
              totalPages: pagination.totalPages || 1,
              hasPrevPage: pagination.hasPrevPage,
              hasNextPage: pagination.hasNextPage,
              onPageChange: setPage,
              onLimitChange: (val) => {
                setLimit(val);
                setPage(1);
              },
              isRtl: isArabic,
            }}
          />
        </div>
      </MDBContainer>

      <OrderDetailsDrawer
        show={drawerOpen && !!selectedOrderDetails}
        onClose={() => {
          setDrawerOpen(false);
          setSelectedOrderDetails(null);
        }}
        orderDetails={selectedOrderDetails}
        onOrderUpdate={() => loadReports()}
        formatDateTime={(value) => {
          const { date, time } = formatLocalizedDateTimeParts(
            value,
            dateLocale,
            { withSeconds: false }
          );
          if (!value) return "";
          return `${date} ${time}`.trim();
        }}
        getExternalOrderId={(order) =>
          order?.external_id || order?.order_id || order?._id || ""
        }
      />
    </div>
  );
}

export default AdminShipmentsPage;
